import { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { getTickets, getTicket, createTicket, replyToTicket } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  MessageCircle, Plus, ArrowLeft, Send, Loader2, Clock, CheckCircle, AlertCircle, XCircle, User, Shield, Bot
} from 'lucide-react';

const statusConfig = {
  open: { label: 'Ouvert', color: 'bg-blue-100 text-blue-700', icon: Clock },
  in_progress: { label: 'En cours', color: 'bg-amber-100 text-amber-700', icon: AlertCircle },
  resolved: { label: 'Resolu', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  closed: { label: 'Ferme', color: 'bg-slate-100 text-slate-600', icon: XCircle },
};

const priorityConfig = {
  low: { label: 'Basse', color: 'bg-slate-100 text-slate-600' },
  medium: { label: 'Moyenne', color: 'bg-blue-100 text-blue-600' },
  high: { label: 'Haute', color: 'bg-red-100 text-red-600' },
};

const categoryLabels = {
  technical: 'Probleme technique',
  billing_question: 'Facturation',
  feature_request: 'Demande fonctionnalite',
  subscription: 'Abonnement / Paiement',
  other: 'Autre',
};

const SupportPage = () => {
  const { language } = useLanguage();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // list | new | detail
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [creating, setCreating] = useState(false);
  const [replying, setReplying] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [form, setForm] = useState({ subject: '', category: 'technical', priority: 'medium', message: '' });
  const messagesEndRef = useRef(null);

  useEffect(() => { loadTickets(); }, []);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [selectedTicket?.messages]);

  const loadTickets = async () => {
    try {
      const res = await getTickets();
      setTickets(res.data);
    } catch (err) { console.error('Failed to load tickets:', err); } finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!form.subject || !form.message) { toast.error('Veuillez remplir le sujet et le message'); return; }
    setCreating(true);
    try {
      await createTicket(form);
      toast.success('Ticket cree avec succes');
      setForm({ subject: '', category: 'technical', priority: 'medium', message: '' });
      setView('list');
      loadTickets();
    } catch { toast.error('Erreur lors de la creation'); } finally { setCreating(false); }
  };

  const handleOpenTicket = async (ticketId) => {
    try {
      const res = await getTicket(ticketId);
      setSelectedTicket(res.data);
      setView('detail');
    } catch { toast.error('Erreur chargement ticket'); }
  };

  const handleReply = async () => {
    if (!replyText.trim()) return;
    setReplying(true);
    try {
      await replyToTicket(selectedTicket.id, { message: replyText });
      setReplyText('');
      const res = await getTicket(selectedTicket.id);
      setSelectedTicket(res.data);
      loadTickets();
    } catch { toast.error('Erreur envoi'); } finally { setReplying(false); }
  };

  const StatusBadge = ({ status }) => {
    const cfg = statusConfig[status] || statusConfig.open;
    return <Badge className={`${cfg.color} hover:${cfg.color} text-xs`}>{cfg.label}</Badge>;
  };

  // LIST VIEW
  if (view === 'list') return (
    <div className="space-y-6" data-testid="support-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Support</h1>
          <p className="text-sm text-zinc-400 mt-1">Contactez-nous, nous vous repondons directement ici</p>
        </div>
        <Button onClick={() => setView('new')} className="bg-blue-600 hover:bg-blue-700" data-testid="new-ticket-btn">
          <Plus className="w-4 h-4 mr-2" />Nouveau ticket
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>
      ) : tickets.length === 0 ? (
        <Card className="border-dashed border-zinc-700 bg-zinc-900/50">
          <CardContent className="py-12 text-center">
            <MessageCircle className="w-12 h-12 mx-auto text-zinc-600 mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">Aucun ticket</h3>
            <p className="text-sm text-zinc-400 mb-4">Vous n'avez pas encore contacte le support</p>
            <Button onClick={() => setView('new')} variant="outline">Creer un ticket</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {tickets.map(t => (
            <button key={t.id} onClick={() => handleOpenTicket(t.id)}
              className={`w-full text-left p-4 rounded-lg border transition-colors ${t.unread_by_user ? 'bg-blue-950/30 border-blue-500/40' : 'bg-zinc-900/50 border-zinc-800 hover:border-zinc-700'}`}
              data-testid={`ticket-${t.id}`}>
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-medium text-white truncate text-sm">{t.subject}</p>
                    {t.unread_by_user && <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />}
                  </div>
                  <p className="text-xs text-zinc-500">{categoryLabels[t.category]} - {t.messages?.length || 0} messages - {new Date(t.updated_at).toLocaleDateString('fr-CH')}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Badge className={`${priorityConfig[t.priority]?.color} text-xs`}>{priorityConfig[t.priority]?.label}</Badge>
                  <StatusBadge status={t.status} />
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  // NEW TICKET VIEW
  if (view === 'new') return (
    <div className="space-y-6" data-testid="new-ticket-form">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => setView('list')}><ArrowLeft className="w-5 h-5" /></Button>
        <h1 className="text-2xl font-bold text-white">Nouveau ticket</h1>
      </div>
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardContent className="pt-6 space-y-4">
          <div className="space-y-2">
            <Label>Sujet *</Label>
            <Input value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} placeholder="Decrivez votre probleme en une phrase" data-testid="ticket-subject" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Categorie</Label>
              <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                <SelectTrigger data-testid="ticket-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(categoryLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Priorite</Label>
              <Select value={form.priority} onValueChange={v => setForm({...form, priority: v})}>
                <SelectTrigger data-testid="ticket-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Basse</SelectItem>
                  <SelectItem value="medium">Moyenne</SelectItem>
                  <SelectItem value="high">Haute</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Message *</Label>
            <Textarea value={form.message} onChange={e => setForm({...form, message: e.target.value})} rows={6} placeholder="Decrivez votre probleme en detail..." data-testid="ticket-message" />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => setView('list')}>Annuler</Button>
            <Button onClick={handleCreate} disabled={creating} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-ticket-btn">
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Envoyer
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // DETAIL VIEW (conversation)
  if (view === 'detail' && selectedTicket) return (
    <div className="space-y-4" data-testid="ticket-detail">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => { setView('list'); loadTickets(); }}><ArrowLeft className="w-5 h-5" /></Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-bold text-white truncate">{selectedTicket.subject}</h1>
          <p className="text-xs text-zinc-500">{categoryLabels[selectedTicket.category]}</p>
        </div>
        <StatusBadge status={selectedTicket.status} />
      </div>

      {/* Messages */}
      <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-2">
        {selectedTicket.messages?.map(msg => (
          <div key={msg.id} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[75%] rounded-xl px-4 py-3 ${
              msg.sender === 'admin' ? 'bg-zinc-800 border border-zinc-700' :
              msg.sender === 'ai' ? 'bg-violet-600/15 border border-violet-500/30' :
              'bg-blue-600/20 border border-blue-500/30'
            }`}>
              <div className="flex items-center gap-2 mb-1">
                {msg.sender === 'admin' ? <Shield className="w-3 h-3 text-emerald-400" /> :
                 msg.sender === 'ai' ? <Bot className="w-3 h-3 text-violet-400" /> :
                 <User className="w-3 h-3 text-blue-400" />}
                <span className="text-xs font-medium text-zinc-400">{msg.sender_name}</span>
                <span className="text-xs text-zinc-600">{new Date(msg.created_at).toLocaleString('fr-CH')}</span>
              </div>
              <p className="text-sm text-zinc-200 whitespace-pre-wrap">{msg.content}</p>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Reply */}
      {selectedTicket.status !== 'closed' && (
        <div className="flex gap-2 pt-2 border-t border-zinc-800">
          <Input value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Votre reponse..." className="flex-1"
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleReply(); }}}
            data-testid="ticket-reply-input" />
          <Button onClick={handleReply} disabled={replying || !replyText.trim()} className="bg-blue-600 hover:bg-blue-700" data-testid="ticket-reply-btn">
            {replying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      )}
      {selectedTicket.status === 'closed' && (
        <div className="text-center p-4 bg-zinc-900 rounded-lg border border-zinc-800">
          <p className="text-sm text-zinc-500">Ce ticket est ferme. Creez un nouveau ticket si vous avez besoin d'aide.</p>
        </div>
      )}
    </div>
  );

  return null;
};

export default SupportPage;
